/**
 * Every date the hosted report displays, formatted one way: as a UTC
 * calendar date, never the viewer's local zone.
 *
 * The backend computes the report period and the week navigator's labels in
 * UTC (`weekTitle` in `reportTracking.routes.js`) and stores every date as a
 * `Date`, serialised as a full ISO timestamp (`2026-08-01T00:00:00.000Z`).
 * Reading that back with a LOCAL `toLocaleDateString` shifts the displayed
 * day for any reader west of UTC — a report period that ends
 * `2026-09-07T00:00:00.000Z` reads as "Sep 6" for a reader in the US. This
 * used to be four separate, slightly different implementations (`ReportIntro`,
 * `ReportFooter`, `WorkCards`, `ScanGrid`), one of which additionally could
 * not parse a full timestamp at all (`ScanGrid`'s old `iso.split('-')`, which
 * read the time-and-zone tail as `NaN`). `new Date(iso)` parses either a bare
 * `yyyy-mm-dd` or a full timestamp; the UTC getters below then read it as the
 * calendar date the backend meant, regardless of the viewer's own clock.
 */

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Parses `iso` as a UTC calendar date, or returns null for anything that
 * isn't a valid date string. */
function parseUtcDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** `2026-08-07T...` -> "Aug 7". */
export function formatMonthDay(iso: string | null | undefined): string | null {
  const date = parseUtcDate(iso);
  if (!date) return null;
  return `${MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}`;
}

/** `2026-08-07T...` -> "Aug 7, 2026". */
export function formatMonthDayYear(
  iso: string | null | undefined,
): string | null {
  const date = parseUtcDate(iso);
  if (!date) return null;
  return `${MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
}

/** `2026-08-07T...` -> "Fri, Aug 7". */
export function formatWeekdayMonthDay(
  iso: string | null | undefined,
): string | null {
  const date = parseUtcDate(iso);
  if (!date) return null;
  return `${WEEKDAYS[date.getUTCDay()]}, ${MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}`;
}

/** "{Aug 1} – {Aug 7}, {2026}" — the report period's own range format. Empty
 * when either end is missing, so `ReportIntro` can drop the whole clause
 * cleanly rather than showing a dangling dash. */
export function formatDateRange(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  if (!start || !end) return '';
  const startLabel = formatMonthDay(start);
  const endDate = parseUtcDate(end);
  if (!startLabel || !endDate) return '';
  return `${startLabel} – ${MONTHS[endDate.getUTCMonth()]} ${endDate.getUTCDate()}, ${endDate.getUTCFullYear()}`;
}
